"use client";

import classNames from "classnames";

import isEmpty from "lodash/isEmpty";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

import StyledButton from "@stadium/ui/src/components/button/styled-button";
import HorizontalCarouselWrapper from "@components/swagmagic/dynamic/horizontal-carousel-wrapper";
import { CatalogEntriesProps } from "@components/swagmagic/dynamic/slices/catalog-entries";

import LinkWithLang from "@components/swagmagic/dynamic/slices/client-link-with-lang";
import Pill from "@stadium/ui/src/components/pill";
import InfoCard from "@components/swagmagic/dynamic/cards/category/info-card";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";
import { MobileScrollWrapper } from "@components/swagmagic/dynamic/mobile-scroll-wrapper";

import {
  CE_12,
  getCarouselSlidesPerRow,
  getHeaderButtonLinkDataAndType,
} from "@helpers/plasmic/slices";
import {
  getCategoryLinkQueryFromFilterOptions,
  sortByCustomOrdering,
} from "@helpers/plasmic/catalog-entries";

import { CATEGORY_PRODUCT_LISTING_LINK_TYPE } from "@stadium/shared/src/constants/stores/routes";
import { ALL_PRODUCTS, ALL_SUBCATEGORY_CODE } from "@constants/routes";

import styles from "./main.module.scss";

export interface CatalogEntriesPillCarouselProps extends CatalogEntriesProps {
  categoriesToRender: any[];
}

const CatalogEntriesPillCarousel = (props: CatalogEntriesPillCarouselProps) => {
  const {
    className,
    title,
    description,
    btnText,
    btnLinkType,
    btnLinkOtherUrl,
    btnLinkCategorySlug,
    linkToNewTab,
    categoriesToRender,
    hideCategories,
    visibleCategoryCount,
    orderCategories,
    linkRouteDefaults,
  } = props;

  const { isDesktopDevice: isDesktop } = useContext(CurrentDeviceContext);

  const pillScrollRef = useRef<HTMLDivElement>(null);
  const pillThumbRef = useRef<HTMLDivElement>(null);
  const [isPillScrollable, setIsPillScrollable] = useState(false);

  const updatePillScrollThumb = useCallback(() => {
    if (!pillThumbRef.current || !pillScrollRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = pillScrollRef.current;
    if (scrollWidth <= clientWidth) return;

    const thumbWidthPercent = (clientWidth / scrollWidth) * 100;
    pillThumbRef.current.style.width = `${thumbWidthPercent}%`;

    const maxScroll = scrollWidth - clientWidth;
    const progress = scrollLeft / maxScroll;
    const trackWidth = pillThumbRef.current.parentElement?.offsetWidth || 0;
    const thumbWidth = pillThumbRef.current.offsetWidth;
    pillThumbRef.current.style.transform = `translateX(${progress * (trackWidth - thumbWidth)}px)`;
  }, []);

  const pills = useMemo(
    () =>
      categoriesToRender?.filter(
        (category) =>
          category.code !== ALL_PRODUCTS &&
          category.code !== ALL_SUBCATEGORY_CODE &&
          !isEmpty(category.children),
      ) ?? [],
    [categoriesToRender],
  );
  const firstCode = pills[0]?.code;

  const [selectedParentCode, setSelectedParentCode] = useState<string | undefined>(
    () => firstCode ?? undefined,
  );

  useEffect(() => {
    if (
      pills.length > 0 &&
      (!selectedParentCode || !pills.some((p) => p.code === selectedParentCode))
    ) {
      setSelectedParentCode(pills[0]?.code ?? undefined);
    }
  }, [pills, selectedParentCode]);

  useEffect(() => {
    if (!pillScrollRef.current) return;

    const scrollContainer = pillScrollRef.current;

    const checkScrollable = () => {
      setIsPillScrollable(scrollContainer.scrollWidth > scrollContainer.clientWidth);
    };

    updatePillScrollThumb();
    checkScrollable();

    const resizeObserver = new ResizeObserver(() => {
      updatePillScrollThumb();
      checkScrollable();
    });
    resizeObserver.observe(scrollContainer);

    scrollContainer.addEventListener("scroll", updatePillScrollThumb);

    return () => {
      scrollContainer.removeEventListener("scroll", updatePillScrollThumb);
      resizeObserver.disconnect();
    };
  }, [updatePillScrollThumb, pills, isDesktop, isPillScrollable]);

  const selectedParent = useMemo(
    () => pills.find((p) => p.code === selectedParentCode) ?? pills[0],
    [pills, selectedParentCode],
  );

  const subcategoriesToRender = useMemo(() => {
    const children = selectedParent?.children ?? [];
    const filtered = children.filter(
      (cat: any) =>
        cat?.code && cat.code !== ALL_SUBCATEGORY_CODE && !hideCategories?.includes(cat.code),
    );
    const sorted = sortByCustomOrdering(filtered, orderCategories ?? []);
    if (visibleCategoryCount && visibleCategoryCount > 0) {
      return sorted.slice(0, visibleCategoryCount);
    }
    return sorted;
  }, [selectedParent, hideCategories, orderCategories, visibleCategoryCount]);

  const defaultSlidesPerRow = getCarouselSlidesPerRow(CE_12);

  const linkDataAndType = useMemo(
    () =>
      getHeaderButtonLinkDataAndType({
        linkType: btnLinkType,
        otherUrl: btnLinkOtherUrl,
        categorySlug: btnLinkCategorySlug || selectedParentCode,
      }),
    [btnLinkType, btnLinkOtherUrl, btnLinkCategorySlug, selectedParentCode],
  );

  const renderCardLink = (category: any) => {
    const imageUrl = category.portraitImage?.url || category.portraitImage;
    const imageMobileUrl = category.imageMobile?.url || category.imageMobile;
    const imageTabletUrl = category.imageTablet?.url || category.imageTablet;
    return (
      <LinkWithLang
        {...linkRouteDefaults}
        type={CATEGORY_PRODUCT_LISTING_LINK_TYPE}
        data={category}
        query={getCategoryLinkQueryFromFilterOptions(category?.filterOptions)}
        target={category?.linkToNewTab ? "_blank" : "_self"}
        key={category.code}
      >
        <InfoCard
          variant={CE_12}
          src={imageUrl}
          srcMobile={imageMobileUrl}
          srcTablet={imageTabletUrl}
          title={category.name}
        />
      </LinkWithLang>
    );
  };

  return (
    <HorizontalCarouselWrapper
      className={`${styles.horizontalCarouselWrapper} ${className ?? ""}`}
      title={title}
      description={description}
      btnText={btnText}
      linkToNewTab={linkToNewTab}
      slidesPerRow={defaultSlidesPerRow}
      linkDataAndType={linkDataAndType}
      linkRouteDefaults={linkRouteDefaults}
      showWithCarousel={isDesktop}
      headerChildren={
        pills.length > 0 ? (
          <>
            <div
              ref={pillScrollRef}
              className={styles.pillList}
              role="tablist"
              aria-label="Category filters"
            >
              {pills.map((category) => {
                const isSelected = category.code === selectedParentCode;
                return (
                  <StyledButton
                    key={category.code}
                    styleType="no-style"
                    className={styles.pillBtn}
                    onClick={() => setSelectedParentCode(category.code)}
                  >
                    <Pill
                      color={isSelected ? "black" : "light"}
                      className={classNames(styles.categoryPill, {
                        [styles.notSelectedCategoryPill]: !isSelected,
                      })}
                    >
                      {category.name}
                    </Pill>
                  </StyledButton>
                );
              })}
            </div>
            {isPillScrollable && (
              <div className={styles.pillScrollTrack}>
                <div ref={pillThumbRef} className={styles.pillScrollThumb} />
              </div>
            )}
          </>
        ) : null
      }
    >
      {isDesktop ? (
        subcategoriesToRender?.map(renderCardLink)
      ) : (
        <MobileScrollWrapper
          key={selectedParentCode ?? "empty"}
          containerClassName={styles.mobileScrollContainer}
          scrollTrackClassName={styles.scrollTrack}
          scrollThumbClassName={styles.scrollThumb}
          depKey={subcategoriesToRender.length}
        >
          {subcategoriesToRender?.map((category) => (
            <div key={category.code} className={styles.mobileCardWrapper}>
              {renderCardLink(category)}
            </div>
          ))}
        </MobileScrollWrapper>
      )}
    </HorizontalCarouselWrapper>
  );
};

export default CatalogEntriesPillCarousel;
